import React from 'react'
import { useQuery } from '@apollo/client'
import { debounce, isFinite, map } from 'lodash'
import { useRouter } from 'next/router'

import { Catalog, Meta } from '~/components'
import { catalogSearchQuery } from '~/gql/queries'
import { useVendor } from '~/hooks'
import { useDynamicPageLimit } from '~/hooks/useDynamicPageLimit'
import { MainLayout } from '~/layouts'
import { createQueryUpdater, notifySentry } from '~/utils'
import type { GetMetaInfoResult } from '~/utils/get-meta-info'

import Header from './header'

const mapFiltersForQuery = filters => map(filters, (value: string, key: string) => `${key}:${value}`)

interface SearchScreenProps {
  metaInfo?: GetMetaInfoResult
  isParentCategory?: boolean
  initialFilters?: any
  term?: string
  categoryName?: string
  subcategoryName?: string
}

const SearchScreen = (props: SearchScreenProps) => {
  const { metaInfo, isParentCategory, term = '', initialFilters, subcategoryName, categoryName } = props

  const router = useRouter()

  const vendor = useVendor()

  const dynamicPageLimit = useDynamicPageLimit()

  const paginationType = vendor.settings?.catalog?.products?.paginationType ?? 'infinite'

  // Read page from URL query params dynamically (updates on shallow routing)
  const currentPage = React.useMemo(() => {
    const pageParam = router.query.page
    if (typeof pageParam === 'string' && isFinite(Number(pageParam))) {
      const pageNum = Number(pageParam)

      return pageNum > 0 ? pageNum : 1
    }

    return 1
  }, [router.query.page])

  const filters = mapFiltersForQuery(initialFilters)

  const queryVariables = React.useMemo(
    () => ({
      term,
      page: currentPage,
      filters,
      ...(paginationType === 'pages' && { limit: dynamicPageLimit }),
    }),
    [filters, term, currentPage, paginationType, dynamicPageLimit],
  )

  const catalogSearchQueryResponse = useQuery(catalogSearchQuery, {
    variables: queryVariables,
    onError: apolloError => notifySentry(apolloError),
  })

  const paginationInfo = catalogSearchQueryResponse.data?.catalogSearch?.pageInfo ?? {}

  const products = catalogSearchQueryResponse.data?.catalogSearch?.items ?? []

  const headerTitle = subcategoryName || categoryName || `"${term}"`

  const fetchMoreProducts = React.useCallback(
    async (page: number) => {
      await catalogSearchQueryResponse.fetchMore({
        updateQuery: createQueryUpdater(paginationType),
        variables: {
          ...queryVariables,
          page,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryVariables, paginationType],
  )

  return (
    <>
      <Meta
        title={metaInfo.title}
        image={metaInfo.image}
        url={metaInfo.url}
        description={metaInfo.description}
        asCanonical={isParentCategory}
        prevPage={paginationInfo.prevPage}
        nextPage={paginationInfo.nextPage}
      />
      <MainLayout loading={catalogSearchQueryResponse.loading}>
        <Header title={headerTitle} numOfHints={paginationInfo.total} />
        <Catalog
          products={products}
          paginationType={paginationType}
          pageInfo={paginationInfo}
          pagination={
            paginationType === 'infinite'
              ? {
                  pageStart: paginationInfo.page ?? 1, // need to specify correct page from pagination info when the user has navigated to another page and then goes back and continue scrolling to avoid loading duplicated products
                  hasMore: paginationInfo.hasNextPage,
                  initialLoad: true,
                  loadMore: debounce(fetchMoreProducts, 500), // When the debounce is removed, the package requests page 2 and 3 when it should only request page 2
                }
              : undefined
          }
        />
      </MainLayout>
    </>
  )
}

export default SearchScreen
